import { IconButton, TextButton } from '@oneaudi/unified-web-components';
import { useEffect } from 'react';
import { Controls } from '~/app/compositions/content/gallery/gallery';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useCtxConfigurator } from '~/app/pages/configurator/context/ctx-configurator';
import { useBreakPoint } from '~/hooks/responsive/useBreakPoint';
import { DATA_TEST_ID } from '~/test/data-test-id';

export const ControlsRight = ({
	activeIndex,
	imagesLength,
	onPrevious,
	onNext,
	controls,
	fullscreen,
	handleFullScreenChange,
}: {
	activeIndex: number;
	imagesLength: number;
	onPrevious: () => void;
	onNext: () => void;
	controls?: Controls;
	fullscreen: boolean;
	handleFullScreenChange: (fullscreen: boolean) => void;
}) => {
	const { isMobile, isMobileView } = useBreakPoint();
	const { i18n } = useI18n();

	const handleResize = () => {
		if ((isMobile || isMobileView) && fullscreen) {
			handleFullScreenChange(false);
		}
	};

	useEffect(() => {
		window?.addEventListener('resize', handleResize);
		return () => window?.removeEventListener('resize', handleResize);
	}, [isMobile, isMobileView, fullscreen]);

	return (
		<>
			<div className={'controls-right'}>
				{controls?.fullscreen && !fullscreen && !(isMobile || isMobileView) && (
					<IconButton
						className="control-button"
						variant="tertiary"
						size="sm"
						icon={'resize-increase'}
						onClick={() => handleFullScreenChange(true)}
						aria-label={i18n('ngc.actions.openFullscreen')}
						data-testid={DATA_TEST_ID.GALLERY_FULL_SCREEN_BUTTON}
					/>
				)}
				<IconButton
					className="control-button"
					variant="tertiary"
					size="sm"
					icon={'back'}
					onClick={onPrevious}
					disabled={activeIndex === 0}
					aria-label={i18n('ngc.actions.previous')}
					data-testid={DATA_TEST_ID.GALLERY_PREVIOUS_BUTTON}
				/>
				<IconButton
					className="control-button"
					variant="tertiary"
					size="sm"
					icon={'forward'}
					onClick={onNext}
					disabled={activeIndex === imagesLength - 1}
					aria-label={i18n('ngc.actions.next')}
					data-testid={DATA_TEST_ID.GALLERY_NEXT_BUTTON}
				/>
			</div>
			{fullscreen && (
				<IconButton
					className="control-button close-fullscreen"
					variant="tertiary"
					size="sm"
					icon={'cancel'}
					aria-label={i18n('ngc.actions.closeFullscreen')}
					onClick={() => handleFullScreenChange(false)}
				/>
			)}
		</>
	);
};

export const ControlsLeft = ({
	id,
	controls,
	toggleCompareMode,
	trackAvpClick,
}: {
	id: string;
	controls: Controls;
	toggleCompareMode: (manualEnableComparison?: boolean) => void;
	trackAvpClick: (open: boolean) => void;
}) => {
	const { i18n } = useI18n();
	const { isMobile, isMobileView } = useBreakPoint();
	const { setActive3DViews, setHideAVP, AVPSessionId } = useCtxConfigurator();
	const { config } = useMarketConfig();

	const enableAvp = () => {
		if (!AVPSessionId) {
			setActive3DViews([id]);
		}
		setHideAVP(false);
		toggleCompareMode(false);
		trackAvpClick(true);
	};

	return (
		<div className={'controls-left'}>
			{config.hasJetStream &&
				config.enable3d &&
				controls.avp &&
				(isMobile || isMobileView ? (
					<IconButton
						className="control-button"
						variant="tertiary"
						size="sm"
						icon={'view-360'}
						onClick={enableAvp}
						aria-label={i18n('ngc.actions.openAVP')}
						data-testid={DATA_TEST_ID.GALLERY_3D_BUTTON}
					/>
				) : (
					<TextButton
						className="control-button"
						variant="tertiary"
						size="sm"
						onClick={enableAvp}
						aria-label={i18n('ngc.actions.openAVP')}
						data-testid={DATA_TEST_ID.GALLERY_3D_BUTTON}>
						{i18n('ngc.avp.galleryControl')}
					</TextButton>
				))}
			{controls.compare && (
				<IconButton
					className="control-button"
					variant="tertiary"
					size="sm"
					icon={'compare'}
					onClick={() => toggleCompareMode()}
					aria-label={i18n('ngc.actions.compare')}
					data-testid={DATA_TEST_ID.GALLERY_COMPARE_BUTTON}
				/>
			)}
		</div>
	);
};
